import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getDispute, resolveDispute, cancelDispute } from "@/services/disputes.service";
import { disputeCaps } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtMoney, fmtDateTime, statusColor } from "@/lib/format";
import ExpandableText from "@/components/shared/ExpandableText";
import { usePolling } from "@/lib/usePolling";

const DisputeDetail = ({ disputeId, onClose, onResolved }) => {
  const [data, setData] = useState(null);
  const [resOpen, setResOpen] = useState(false);
  const [resolution, setResolution] = useState("ReleaseToPayee");
  const [split, setSplit] = useState(50);

  const load = async () => {
    try { setData(await getDispute(disputeId)); } catch {}
  };
  useEffect(() => { if (disputeId) load(); /* eslint-disable-next-line */ }, [disputeId]);
  usePolling(() => { if (disputeId) load(); });

  const resolve = async () => {
    try {
      await resolveDispute(disputeId, { resolution, split_percentage_to_payee: resolution === "Split" ? parseFloat(split) : null });
      toast.success("Dispute resolved");
      setResOpen(false);
      onResolved && onResolved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const cancel = async () => {
    try {
      await cancelDispute(disputeId);
      toast.success("Dispute cancelled");
      onResolved && onResolved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={!!disputeId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="dispute-detail-modal">
        <DialogHeader><DialogTitle>Dispute {data?.dispute_id}</DialogTitle></DialogHeader>
        {!data ? (
          <div className="text-muted-foreground py-6 text-sm">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusColor(data.status)}`}>{data.status}</span>
              <Link to={`/contracts/${data.contract_id}`} className="text-sm font-medium text-primary hover:underline">{data.contract_title}</Link>
              <span className="text-sm text-muted-foreground">· {data.milestone_title}</span>
              <span className="text-sm font-display font-bold ml-auto">{fmtMoney(data.amount)}</span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Reason</div>
                <ExpandableText text={data.reason} title="Reason" className="mt-0.5 font-medium" testid="dispute-reason" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Desired outcome</div>
                <div className="mt-0.5 font-medium">{data.desired_outcome || "—"}</div>
              </div>
            </div>
            {data.details && (
              <div className="bg-accent/30 border border-border rounded-lg p-3 text-sm">
                <ExpandableText text={data.details} title="Details" clamp="line-clamp-4" testid="dispute-details" />
              </div>
            )}
            {data.evidence_link && (
              <a href={data.evidence_link} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline break-all">{data.evidence_link}</a>
            )}
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Timeline</div>
              <ol className="space-y-2" data-testid="dispute-timeline">
                {(data.timeline || []).map((ev, i) => (
                  <li key={i} className="flex gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                    <div className="flex-1">
                      <div className="text-sm"><span className="font-semibold">{ev.event}</span> <span className="text-muted-foreground">by {ev.by_name}</span></div>
                      {ev.note && <ExpandableText text={ev.note} title={ev.event} className="text-xs text-muted-foreground mt-0.5" />}
                      <div className="text-[11px] text-muted-foreground mt-0.5">{fmtDateTime(ev.at)}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            {disputeCaps(data).can_resolve && (
              <Button onClick={() => setResOpen(true)} className="w-full" data-testid="detail-resolve-btn">Resolve dispute</Button>
            )}
            {disputeCaps(data).can_cancel && (
              <Button variant="outline" onClick={cancel} className="w-full" data-testid="detail-cancel-dispute-btn">Cancel dispute</Button>
            )}
            <Dialog open={resOpen} onOpenChange={setResOpen}>
              <DialogContent>
                <DialogHeader><DialogTitle>Resolve</DialogTitle></DialogHeader>
                <div className="space-y-2">
                  {[{ v: "ReleaseToPayee", l: "Release to payee" }, { v: "RefundToPayer", l: "Refund to payer" }, { v: "Split", l: "Split" }].map((o) => (
                    <label key={o.v} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${resolution === o.v ? "border-primary bg-accent" : "border-border hover:bg-secondary/50"}`}>
                      <input type="radio" checked={resolution === o.v} onChange={() => setResolution(o.v)} className="accent-primary" />
                      <span className="text-sm font-medium">{o.l}</span>
                    </label>
                  ))}
                  {resolution === "Split" && (
                    <div className="pt-2">
                      <Label>% to payee</Label>
                      <Input type="number" min="0" max="100" value={split} onChange={(e) => setSplit(e.target.value)} className="mt-1.5 h-11" />
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button onClick={resolve} data-testid="confirm-resolve-btn">Confirm</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DisputeDetail;
